import { createContext, FC, useContext, useEffect, useState } from 'react';
import { Answer, InvestigationDetails, Question } from '@/types/QuestionTypes';
import { DataContext } from './DataContext';
import { INVESTIGATION_ANSWERED_BY, INVESTIGATION_STATUS } from '@/types/GeneralTypes';
import { config } from '@branding/config';
// import { INVESTIGATION_STATUS } from '@refineintel/types'; // TODO: Import this from refine/types
import { useLocation } from 'react-router-dom';
import { progressBarConstants } from 'src/Config/constantValues';
import { RFIAttachment } from '@refineintel/types';
import { LocalizationContext } from './LocalizationContext';
import { usePostHog } from 'posthog-js/react';
import { SessionMetrics } from '@components/Questions/Question';
import { useSetUserMetrics } from '@hooks/useSetUserMetrics';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from 'src/types/RefineAnalyticsEvents';
import { useQueryGetBranchInvestigationDetails } from '@hooks/useQueryGetBranchInvestigationDetails';
import { useQueryCompleteInvestigation } from '@hooks/useQueryCompleteInvestigation';

type InvestigationStateObject = {
	investigationDetails: InvestigationDetails | null;
	addAnswerToInvestigationDetails: Function;
	toPreviousScreen: Function;
	isError: string | null;
	raiseError: (error: string) => void;
	isLoading: boolean;
	investigationStatus: INVESTIGATION_STATUS;
	updateInvestigationStatus: Function;
	userLanguage: string;
	attachments: RFIAttachment[];
	updateAttachments: (attachments: RFIAttachment[]) => void;
	removeAttachments?: (attachmentNames: string[]) => void;
	refreshInvestigationDetails: (forceRefresh?: boolean) => void;
	setSupportLandscape: (supportLandscape: boolean) => void;
	supportLandscape: boolean;
};

const defaultInvestigationState: InvestigationStateObject = {
	investigationDetails: null,
	addAnswerToInvestigationDetails: (answer: Answer) => {},
	toPreviousScreen: () => {},
	isError: null,
	raiseError: (error: string) => {},
	isLoading: false,
	userLanguage: '',
	investigationStatus: INVESTIGATION_STATUS.BEGINNING,
	updateInvestigationStatus: () => {},
	attachments: [] as RFIAttachment[],
	updateAttachments: (attachments: RFIAttachment[]) => {},
	removeAttachments: (attachmentNames: string[]) => {},
	refreshInvestigationDetails: () => {},
	setSupportLandscape: (supportLandscape: boolean) => {},
	supportLandscape: false,
};

export const InvestigationContext = createContext<InvestigationStateObject>(defaultInvestigationState);

const InvestigationProvider: FC<any> = ({ children }) => {
	// Investigation Details
	const [investigationDetails, setInvestigationDetails] = useState<InvestigationDetails | null>(
		defaultInvestigationState.investigationDetails,
	);
	const posthog = usePostHog();
	const { LanguageCode, languageChanged, setLanguageChanged, setLanguage } = useContext(LocalizationContext);
	const { pathname } = useLocation();
	const sessionID = posthog?.get_session_id();
	const { trackRefineEvent } = useAnalytics();
	const [supportLandscape, setSupportLandscape] = useState<boolean>(false);

	const { postCompleteInvestigation } = useQueryCompleteInvestigation();

	const [userLanguage, setUserLanguage] = useState<string>('');

	// Error
	const [isError, setIsError] = useState<string | null>(null);
	const { setUserMetrics } = useSetUserMetrics();

	const raiseError = (error: string) => {
		setIsError(error);
	};
	// Loading
	const [isLoading, setIsLoading] = useState<boolean>(false);
	// Investigation Status
	const [investigationStatus, setInvestigationStatus] = useState<INVESTIGATION_STATUS>(
		(investigationDetails?.status as INVESTIGATION_STATUS) || defaultInvestigationState.investigationStatus,
	);
	// Authentication
	const { sc } = useContext(DataContext); // Get refine token from context
	// Attachments
	const [attachments, setAttachments] = useState<RFIAttachment[]>([]);
	const [refreshInvestigationDetailsFlag, setInvestigationDetailsFlag] = useState<boolean>(false);
	useEffect(() => {
		setAttachments(investigationDetails?.nextQuestion?.additionalOptions?.attachments || []);
	}, []);

	const addAnswerToInvestigationDetails = (response: any, answer: any) => {
		if (investigationDetails === null) return;

		const newInvestigationDetails: InvestigationDetails = { ...investigationDetails! };
		const currentQuestion: Question = (investigationDetails! as InvestigationDetails).nextQuestion as Question;
		const currentQID = currentQuestion.qid;
		newInvestigationDetails.nextQuestion = response.nextQuestion;
		newInvestigationDetails.status = response.status;
		updateInvestigationStatus(response.status);
		// @ts-ignore
		newInvestigationDetails.questionsAndAnswersHistory[currentQID] = {
			question: currentQuestion,
			answer: answer,
		};

		if (
			newInvestigationDetails.questionsHistory === null ||
			newInvestigationDetails.questionsHistory === undefined ||
			Object.keys(newInvestigationDetails.questionsHistory)?.length === 0
		) {
			newInvestigationDetails.questionsHistory = [currentQID];
			trackRefineEvent(REFINE_ANALYTICS_EVENTS.FIRST_QUESTION_ANSWERED);
		} else {
			newInvestigationDetails.questionsHistory.push(currentQID);
		}
		setInvestigationDetails(newInvestigationDetails);
	};

	const toPreviousScreen = () => {
		if (investigationDetails === null) return;
		const newInvestigationDetails = { ...investigationDetails! };
		let progress = 0;

		if (investigationDetails) {
			// Beginning state
			if (Object.keys(investigationDetails.questionsAndAnswersHistory).length === 0) {
				newInvestigationDetails.status = INVESTIGATION_STATUS.BEGINNING;
				updateInvestigationStatus(INVESTIGATION_STATUS.BEGINNING);
				progress = progressBarConstants.welcomeScreen;
			} else if (
				// In progress state
				investigationDetails.status === INVESTIGATION_STATUS.INPROGRESS ||
				investigationDetails.status === INVESTIGATION_STATUS.FINALQUESTION
			) {
				const lastQid = investigationDetails.questionsHistory[investigationDetails.questionsHistory.length - 1];
				newInvestigationDetails.questionsHistory.pop();
				// @ts-ignore
				const lastQ = newInvestigationDetails.questionsAndAnswersHistory[lastQid];
				if (newInvestigationDetails.questionsHistory.length === 0) {
					progress = progressBarConstants.firstQuestion;
				} else {
					progress = lastQ?.question.priorProgress;
				}
				// @ts-ignore
				delete newInvestigationDetails.questionsAndAnswersHistory[lastQid];
				// Update status depending on the last question
				if (lastQ?.question) {
					newInvestigationDetails.nextQuestion = lastQ.question;
					updateInvestigationStatus(INVESTIGATION_STATUS.INPROGRESS);
					newInvestigationDetails.status = INVESTIGATION_STATUS.INPROGRESS;
				} else {
					// If there is no last question, then we are at the beginning
					updateInvestigationStatus(INVESTIGATION_STATUS.BEGINNING);
					newInvestigationDetails.status = INVESTIGATION_STATUS.BEGINNING;
				}
			} else if (investigationDetails.status === INVESTIGATION_STATUS.UPLOADFILE) {
				newInvestigationDetails.status = INVESTIGATION_STATUS.FINALQUESTION;
				updateInvestigationStatus(INVESTIGATION_STATUS.FINALQUESTION);
				const lastQid = investigationDetails.questionsHistory[investigationDetails.questionsHistory.length - 1];
				newInvestigationDetails.questionsHistory.pop();
				// @ts-ignore
				const lastQ = newInvestigationDetails.questionsAndAnswersHistory[lastQid];
				// progress = lastQ.question.priorProgress;
				progress = progressBarConstants.uploadFiles;
				newInvestigationDetails.nextQuestion = lastQ?.question;
			}
			setInvestigationDetails(newInvestigationDetails as InvestigationDetails);
			return progress;
		}
	};

	const updateInvestigationStatus = (status: INVESTIGATION_STATUS) => {
		setInvestigationStatus(status);
	};

	const getCustomerName = (outreachText: string | undefined) => {
		if (!outreachText || outreachText.length < 6) return '';
		return outreachText.substring(5).substring(0, outreachText.indexOf(' '));
	};

	const updateAttachments = (attachments: RFIAttachment[]) => {
		if (attachments) {
			setAttachments(attachments);
		}
	};

	// TODO: Toggle flag for force refresh the investigation details
	const refreshInvestigationDetails = (forceRefresh: boolean = false) => {
		if (!forceRefresh) {
			if (attachments.length > 0) {
				setInvestigationDetailsFlag((prev) => !prev);
			}
		} else {
			setInvestigationDetailsFlag((prev) => !prev);
		}
	};

	const calculateTimeOnScreen = (): {
		componentType: 'investigationContext' | 'question';
		timeSpent: number;
		questionId: string;
	} => {
		let currentQuestion = sessionStorage.getItem('currentQuestionId');
		const investigationContextOpened = JSON.parse(sessionStorage.getItem('investigationContextOpened') ?? '0');
		const type = investigationContextOpened ? 'investigationContext' : `question`;
		const storedMetrics = JSON.parse(sessionStorage.getItem('sessionMetrics') ?? '[]') as SessionMetrics[];
		const currentMetric = storedMetrics.find((metric) => metric.questionId === currentQuestion);
		let timeSpent = 0;
		if (currentMetric) {
			timeSpent = new Date().getTime() - currentMetric.openedTime;
		}
		if (type === 'investigationContext') {
			timeSpent = new Date().getTime() - investigationContextOpened;
			currentQuestion = 'investigationContext';
		}

		return {
			componentType: type,
			timeSpent,
			questionId: currentQuestion ?? 'completed',
		};
	};

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				setLanguageChanged(true);
				setInvestigationDetailsFlag((prev) => !prev);
			}
			if (document.visibilityState === 'hidden') {
				const { componentType, timeSpent, questionId } = calculateTimeOnScreen();
				if (questionId === 'completed') return;
				setUserMetrics({
					componentType,
					timeSpent,
					questionId,
					actionType: 'unfocusedTab',
					sc: sc,
				});
			}
		};
		const handleBeforeUnload = () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);

			const { componentType, timeSpent, questionId } = calculateTimeOnScreen();
			if (questionId === 'completed') return;
			setUserMetrics({
				componentType,
				timeSpent,
				questionId,
				actionType: 'closedTab',
				sc: sc,
			});
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			window.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	const { getInvestigationDetails } = useQueryGetBranchInvestigationDetails();

	useEffect(() => {
		const fetchInvestigationDetails = async () => {
			setIsLoading(true);

			const investigation = await getInvestigationDetails({
				userLanguage: !userLanguage ? '' : LanguageCode,
				isLanguageChanged: languageChanged,
			});

			setIsLoading(false);

			if (!investigation) {
				raiseError('InvestigationDetails is null');
				return;
			}

			const newInvestigationDetails = {
				...investigation,
			} as InvestigationDetails;

			if (investigation.status === INVESTIGATION_STATUS.EXPIRED) {
				newInvestigationDetails.questionsAndAnswersHistory = [];
			}

			if (
				investigation &&
				(investigation.investigationType === 'oucf' || investigation.investigationType === 'edd') &&
				investigation.nextQuestion.qid === 'finalOptionalQuestion'
			) {
				let requestBody: any = {
					answeredBy: INVESTIGATION_ANSWERED_BY.CLIENT,
					emotionalState: null,
					notes: '',
					sc: sc,
				};
				const res = await postCompleteInvestigation({
					requestBody,
				});
				if (res) {
					newInvestigationDetails.status = INVESTIGATION_STATUS.COMPLETED;
				} else {
					raiseError('Error completing investigation');
				}
			}

			if (investigation.status === INVESTIGATION_STATUS.COMPLETED) {
				newInvestigationDetails.questionsHistory = [];
			} else if (investigation.questionsAndAnswersHistory) {
				newInvestigationDetails.questionsHistory = Object.keys(investigation.questionsAndAnswersHistory).sort();
			}

			setInvestigationDetails(newInvestigationDetails);
			setLanguageChanged(false);
			const decodedChannel = atob(newInvestigationDetails.channel || '');

			if (!userLanguage) {
				const investigationLanguage = newInvestigationDetails.lang;
				setLanguage(investigationLanguage);
				setUserLanguage(investigationLanguage);
			}

			if (posthog && !config.isDevelopment) {
				posthog.identify(decodedChannel || sessionID, {
					email: decodedChannel,
					name: getCustomerName(newInvestigationDetails?.bankerOutreachText),
					sessionID: sessionID,
					isOUCF: newInvestigationDetails?.investigationType === 'oucf',
					isDirectEDD: newInvestigationDetails?.investigationType === 'edd',
				});
			}
		};

		fetchInvestigationDetails();
	}, [refreshInvestigationDetailsFlag, LanguageCode]);

	useEffect(() => {
		if (investigationDetails && !attachments?.length) {
			setAttachments(investigationDetails?.nextQuestion?.additionalOptions?.attachments || []);
		}
	}, [investigationDetails]);

	return (
		<InvestigationContext.Provider
			value={{
				investigationDetails,
				addAnswerToInvestigationDetails,
				toPreviousScreen,
				isError,
				raiseError,
				isLoading,
				investigationStatus,
				updateInvestigationStatus,
				attachments,
				userLanguage,
				updateAttachments,
				refreshInvestigationDetails,
				setSupportLandscape,
				supportLandscape,
			}}>
			{children}
		</InvestigationContext.Provider>
	);
};

export default InvestigationProvider;
